
import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'

export interface SharedContent {
  type: 'post' | 'drama'
  id: string
  title: string
  description: string
  image?: string
  url: string
  meta?: {
    category?: string
    categoryColor?: string
    author?: string
    rating?: number
    year?: number
    episodes?: number
    genre?: string
  }
}

interface ConversationOption {
  id: string
  userId: string
  username: string
  avatarUrl: string | null
}

interface ShareToMessageModalProps {
  isOpen: boolean
  onClose: () => void
  content: SharedContent | null
  onSend: (conversationId: string, content: SharedContent) => Promise<void>
  onStartNewConversation: (userId: string, content: SharedContent) => Promise<void>
}

const ShareToMessageModal = ({
  isOpen,
  onClose,
  content,
  onSend,
  onStartNewConversation
}: ShareToMessageModalProps) => {
  const { user } = useAuth()
  const [conversations, setConversations] = useState<ConversationOption[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<
    { id: string; username: string; avatar_url: string | null }[]
  >([])
  const [loading, setLoading] = useState(true)
  const [searching, setSearching] = useState(false)
  const [sending, setSending] = useState<string | null>(null)
  const [sent, setSent] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState<'recent' | 'search'>('recent')

  useEffect(() => {
    if (isOpen && user) {
      fetchConversations()
      setSent([])
      setSearchQuery('')
      setSearchResults([])
      setActiveTab('recent')
    }
  }, [isOpen, user])

  const fetchConversations = async () => {
    if (!user) return
    setLoading(true)
    try {
      const { data: userConvs } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', user.id)

      if (!userConvs || userConvs.length === 0) {
        setConversations([])
        setLoading(false)
        return
      }

      const convIds = userConvs.map(c => c.conversation_id)

      const { data: participants } = await supabase
        .from('conversation_participants')
        .select('conversation_id, user_id, user_profiles(username, avatar_url)')
        .in('conversation_id', convIds)
        .neq('user_id', user.id)

      if (participants) {
        const convOptions: ConversationOption[] = participants.map((p: any) => ({
          id: p.conversation_id,
          userId: p.user_id,
          username: p.user_profiles?.username || 'Unknown',
          avatarUrl: p.user_profiles?.avatar_url || null
        }))
        setConversations(convOptions)
      }
    } catch (err) {
      console.error('Error fetching conversations:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async (query: string) => {
    setSearchQuery(query)
    if (query.trim().length < 2) {
      setSearchResults([])
      return
    }
    setSearching(true)
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, username, avatar_url')
        .neq('id', user?.id || '')
        .ilike('username', `%${query.trim()}%`)
        .limit(10)

      if (data) setSearchResults(data)
    } catch (err) {
      console.error('Search error:', err)
    } finally {
      setSearching(false)
    }
  }

  const handleSendToConversation = async (convId: string) => {
    if (!content || sending) return
    setSending(convId)
    try {
      await onSend(convId, content)
      setSent(prev => [...prev, convId])
    } catch (err) {
      console.error('Error sending:', err)
    } finally {
      setSending(null)
    }
  }

  const handleSendToUser = async (userId: string) => {
    if (!content || sending) return
    setSending(userId)
    try {
      await onStartNewConversation(userId, content)
      setSent(prev => [...prev, userId])
    } catch (err) {
      console.error('Error sending:', err)
    } finally {
      setSending(null)
    }
  }

  if (!isOpen || !content) return null

  const filteredConversations = conversations.filter(c =>
    c.username.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">Send in Message</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg text-gray-500"></i>
          </button>
        </div>

        {/* Content Preview */}
        <div className="px-5 pt-4 pb-3">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
            {content.image && (
              <img
                src={content.image}
                alt=""
                className="w-14 h-14 rounded-lg object-cover object-top flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wider ${
                    content.type === 'drama' ? 'text-rose-500' : 'text-teal-600'
                  }`}
                >
                  {content.type === 'drama' ? 'Drama' : 'Forum Post'}
                </span>
                {content.meta?.category && (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full text-white"
                    style={{
                      backgroundColor: content.meta.categoryColor || '#6b7280'
                    }}
                  >
                    {content.meta.category}
                  </span>
                )}
              </div>
              <p className="text-sm font-semibold text-gray-900 line-clamp-1">
                {content.title}
              </p>
              <p className="text-xs text-gray-500 line-clamp-1">
                {content.description}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex px-5 gap-1">
          <button
            onClick={() => setActiveTab('recent')}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
              activeTab === 'recent'
                ? 'bg-gray-900 text-white'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            Recent Chats
          </button>
          <button
            onClick={() => setActiveTab('search')}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
              activeTab === 'search'
                ? 'bg-gray-900 text-white'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            Find User
          </button>
        </div>

        {/* Search */}
        {activeTab === 'search' && (
          <div className="px-5 pt-3">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search by username..."
                className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>
        )}

        {/* List */}
        <div className="max-h-64 overflow-y-auto px-3 py-3">
          {activeTab === 'recent' ? (
            loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
              </div>
            ) : filteredConversations.length > 0 ? (
              filteredConversations.map(conv => {
                const isSent = sent.includes(conv.id)
                const isSending = sending === conv.id
                return (
                  <div
                    key={conv.id}
                    className="flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-semibold text-sm overflow-hidden flex-shrink-0">
                        {conv.avatarUrl ? (
                          <img
                            src={conv.avatarUrl}
                            alt=""
                            className="w-full h-full object-cover object-top"
                          />
                        ) : (
                          conv.username[0]?.toUpperCase() || 'U'
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {conv.username}
                      </span>
                    </div>
                    <button
                      onClick={() => handleSendToConversation(conv.id)}
                      disabled={isSent || isSending}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                        isSent
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:shadow-md'
                      } disabled:opacity-70`}
                    >
                      {isSending ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : isSent ? (
                        <>
                          <i className="ri-check-line mr-1"></i>Sent
                        </>
                      ) : (
                        'Send'
                      )}
                    </button>
                  </div>
                )
              })
            ) : (
              <div className="text-center py-8">
                <i className="ri-message-3-line text-3xl text-gray-300 block mb-2"></i>
                <p className="text-sm text-gray-400">No recent conversations</p>
                <p className="text-xs text-gray-400 mt-1">
                  Use &quot;Find User&quot; to search
                </p>
              </div>
            )
          ) : searching ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
            </div>
          ) : searchResults.length > 0 ? (
            searchResults.map(u => {
              const isSent = sent.includes(u.id)
              const isSending = sending === u.id
              return (
                <div
                  key={u.id}
                  className="flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-semibold text-sm overflow-hidden flex-shrink-0">
                      {u.avatar_url ? (
                        <img
                          src={u.avatar_url}
                          alt=""
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        u.username[0]?.toUpperCase() || 'U'
                      )}
                    </div>
                    <span className="text-sm font-medium text-gray-800 truncate">
                      {u.username}
                    </span>
                  </div>
                  <button
                    onClick={() => handleSendToUser(u.id)}
                    disabled={isSent || isSending}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      isSent
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:shadow-md'
                    } disabled:opacity-70`}
                  >
                    {isSending ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : isSent ? (
                      <>
                        <i className="ri-check-line mr-1"></i>Sent
                      </>
                    ) : (
                      'Send'
                    )}
                  </button>
                </div>
              )
            })
          ) : searchQuery.trim().length >= 2 ? (
            <div className="text-center py-8">
              <i className="ri-user-search-line text-3xl text-gray-300 block mb-2"></i>
              <p className="text-sm text-gray-400">No users found</p>
            </div>
          ) : (
            <div className="text-center py-8">
              <i className="ri-user-search-line text-3xl text-gray-300 block mb-2"></i>
              <p className="text-sm text-gray-400">
                Type at least 2 characters to search
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

export default ShareToMessageModal
export { ShareToMessageModal }
